import { httpClient } from './http-client';

export interface PixPaymentRequest {
  appointmentId: string;
  patientId: string;
  doctorId: string;
  amount: number;
  description?: string;
  returnUrl?: string;
  patientName?: string;
  patientEmail?: string;
  patientCpf?: string;
  captchaToken?: string;
}

export interface CardPaymentRequest {
  appointmentId: string;
  patientId: string;
  doctorId: string;
  amount: number;
  description?: string;
  returnUrl?: string;
  patientName?: string;
  patientEmail?: string;
  patientCpf?: string;
  installments?: number;
  encryptedCard?: string;
  securityCode?: string;
  cardTokenId?: string;
  captchaToken?: string;
}

export interface PaymentResult {
  paymentId: string;
  status: 'PENDING' | 'APPROVED' | 'FAILED' | 'CANCELLED';
  amount: number;
  currency: string;
  paymentMethod: string;
  qrCode?: string;
  qrCodeUrl?: string;
  checkoutUrl?: string;
  createdAt: string;
}

export interface PaymentStatus {
  paymentId: string;
  status: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentListItem {
  paymentId: string;
  status: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedPayments {
  items: PaymentListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface PaymentRefundResult {
  refundId: string;
  paymentId: string;
  amount: number;
  status: 'PENDING' | 'APPROVED' | 'FAILED';
  refundedAt?: string;
}

export const paymentsApi = {
  async createPixPayment(payload: PixPaymentRequest): Promise<PaymentResult> {
    return httpClient.post<PaymentResult>('/payments/pix', payload);
  },

  async createCardPayment(payload: CardPaymentRequest): Promise<PaymentResult> {
    return httpClient.post<PaymentResult>('/payments/card', payload);
  },

  async getPayments(params: {
    patientId?: string;
    doctorId?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedPayments> {
    const searchParams = new URLSearchParams();
    if (params.patientId) searchParams.set('patientId', params.patientId);
    if (params.doctorId) searchParams.set('doctorId', params.doctorId);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return httpClient.get<PaginatedPayments>(`/payments${qs ? `?${qs}` : ''}`);
  },

  async getPaymentStatus(paymentId: string): Promise<PaymentStatus> {
    return httpClient.get<PaymentStatus>(`/payments/${paymentId}`);
  },

  async confirmPayment(paymentId: string): Promise<PaymentStatus> {
    return httpClient.post<PaymentStatus>(`/payments/${paymentId}/confirm`, {});
  },

  async getPaymentByAppointment(appointmentId: string): Promise<PaymentStatus> {
    return httpClient.get<PaymentStatus>(`/payments/appointment/${appointmentId}`);
  },

  async refundPayment(paymentId: string, amount?: number): Promise<PaymentRefundResult> {
    return httpClient.post<PaymentRefundResult>('/payments/refund', { paymentId, amount });
  },

  async getPagBankPublicConfig(): Promise<{ env: string; publicKey: string; softDescriptor: string }> {
    return httpClient.get<{ env: string; publicKey: string; softDescriptor: string }>('/payments/pagbank/public-config');
  },

  async createPagBankPix(payload: {
    referenceId: string;
    paciente: { name: string; email: string; tax_id: string; phone?: string };
    consultaId?: string;
    pacienteId: string;
    valorCentavos: number;
    expiraEmMinutos?: number;
    descricao?: string;
  }): Promise<{
    success: boolean;
    pedidoId: string;
    orderIdPagBank: string;
    referenceId: string;
    valorTotalCentavos: number;
    pixCopiaECola: string;
    pixQrCodeUrl: string;
    expiracao: string;
  }> {
    return httpClient.post('/payments/pagbank/pix', payload);
  },

  async createPagBankCard(payload: {
    referenceId: string;
    paciente: { name: string; email: string; tax_id: string; phone?: string };
    consultaId?: string;
    pacienteId: string;
    valorBaseCentavos: number;
    parcelas?: number;
    repassarJuros?: boolean;
    taxaMensal?: number;
    encryptedCard?: string;
    cardTokenId?: string;
    securityCode?: string;
    storeCard?: boolean;
    descricao?: string;
  }): Promise<any> {
    return httpClient.post('/payments/pagbank/card', payload);
  },

  async getSavedCards(): Promise<Array<{
    id: string;
    cardToken: string;
    bandeira: string;
    primeirosDig: string;
    ultimosDig: string;
    mesExpiracao: number;
    anoExpiracao: number;
    nomeTitular: string;
  }>> {
    return httpClient.get('/payments/pagbank/cards');
  },

  async refundPagBankPayment(payload: {
    pedidoPagamentoId: string;
    valorCentavosParcial?: number;
    motivo?: string;
  }): Promise<any> {
    return httpClient.post('/payments/pagbank/refund', payload);
  },

  async capturePreAuthorization(pedidoPagamentoId: string, valorCentavos?: number): Promise<any> {
    return httpClient.post(`/payments/pagbank/capture/${pedidoPagamentoId}`, { valorCentavos });
  },

  async getDisputes(): Promise<any[]> {
    return httpClient.get<any[]>('/payments/pagbank/disputes');
  },

  async getPagBankInstallmentOptions(amount: number): Promise<{
    config: {
      enabled: boolean;
      enableCreditCard?: boolean;
      enableDebitCard?: boolean;
      debitRatePercent?: number;
      maxInstallments: number;
      interestFreeInstallments: number;
      minInstallmentAmount: number;
      minTotalAmountForInstallments: number;
      monthlyInterestRate: number;
      ratesByInstallment?: Record<number, number>;
      passInterestToCustomer: boolean;
    };
    options: Array<{
      numero: number;
      valorParcelaCentavos: number;
      valorTotalCentavos: number;
      valorParcelaReais: number;
      valorTotalReais: number;
      temJuros: boolean;
      taxaJurosPercent?: number;
      descricao: string;
    }>;
    debit?: {
      enabled: boolean;
      ratePercent: number;
      valorReais: number;
      valorCentavos: number;
      descricao: string;
    };
  }> {
    return httpClient.get(`/payments/pagbank/installments?amount=${amount}`);
  },

  async getPagBankInstallmentConfig(): Promise<{
    enabled: boolean;
    enableCreditCard?: boolean;
    enableDebitCard?: boolean;
    debitRatePercent?: number;
    maxInstallments: number;
    interestFreeInstallments: number;
    minInstallmentAmount: number;
    minTotalAmountForInstallments: number;
    monthlyInterestRate: number;
    ratesByInstallment?: Record<number, number>;
    passInterestToCustomer: boolean;
  }> {
    return httpClient.get('/payments/pagbank/installment-config');
  },

  async updatePagBankInstallmentConfig(data: {
    enabled?: boolean;
    enableCreditCard?: boolean;
    enableDebitCard?: boolean;
    debitRatePercent?: number;
    maxInstallments?: number;
    interestFreeInstallments?: number;
    minInstallmentAmount?: number;
    minTotalAmountForInstallments?: number;
    monthlyInterestRate?: number;
    ratesByInstallment?: Record<number, number>;
    passInterestToCustomer?: boolean;
  }): Promise<any> {
    return httpClient.put('/payments/pagbank/installment-config', data);
  },
};
